import { createContext, useContext } from 'react'
import { LayoutMode } from '@/components/Files/FilesListing.tsx'
import { DriveContact, DriveThemeStyleDefinition, OpenedImageCaption } from '@/models/drive.ts'
import { AlbumPreviewFile } from '@/models/album.ts'
import { DownloadOptions } from '@/models/downloadOptions.ts'

export interface DriveViewAlbum {
  id: string
  album_id: string
  title: string
  description: string
  thumbnail: string | null
  files_count: number
  preview_desktop?: AlbumPreviewFile | null
  preview_mobile?: AlbumPreviewFile | null
  preview_desktop_center_x?: number
  preview_desktop_center_y?: number
  preview_mobile_center_x?: number
  preview_mobile_center_y?: number
}

export interface DriveViewMetadata {
  title: string
  driveTitle: string
  driveType: string
  theme?: DriveThemeStyleDefinition | null
  spacing?: number | null
  layoutMode?: LayoutMode | null
  albumOverview?: 'both' | 'desktop' | 'mobile' | 'none' | null
  albumOverviewStyle?: 'rect' | 'circle' | null
  areElementsContained?: boolean | null
  contact?: DriveContact | null
  userDefinedExpiresAt?: string | null
  openedImageCaption?: OpenedImageCaption | null
  /**
   * Which download resolutions this view may offer. The public payloads carry no
   * watermark flag, so a watermarked gallery has to be restricted through the drive's
   * `download_options` ('detail') to take effect in share/portfolio views as well.
   */
  downloadOptions?: DownloadOptions | null
}

export interface DriveViewContextValue {
  metadata: DriveViewMetadata | null
  albums: DriveViewAlbum[]
  currentAlbum: DriveViewAlbum | null
  isLoading: boolean
  error: Error | null
  onSelectAlbum: (album: DriveViewAlbum) => void
  fetchAlbumFiles: (albumId: string) => Promise<any>
}

export const DriveViewContext = createContext<DriveViewContextValue | null>(null)

export function useDriveViewContext() {
  const context = useContext(DriveViewContext)
  if (!context) {
    throw new Error('useDriveViewContext must be used within a DriveViewProvider')
  }
  return context
}
